import { alpha, createStyles, type Theme } from '@material-ui/core/styles';
import { absenceHatch, cockpitType, confidenceInk } from './cockpitTokens';

const railLayoutStyles = (theme: Theme) =>
  createStyles({
    // the coverage bar is the one full-bleed object on the page; height is what
    // makes proportion readable, so it is set here and nowhere else
    rail: {
      display: 'flex',
      height: theme.spacing(7),
      overflow: 'hidden',
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${alpha(theme.palette.text.primary, 0.22)}`,
      background: theme.palette.background.paper,
    },
    segmentItem: { display: 'flex', minWidth: 0 },
    segment: {
      width: '100%',
      minWidth: 0,
      border: 0,
      borderRight: `1px solid ${theme.palette.background.paper}`,
      padding: 0,
      cursor: 'pointer',
      overflow: 'hidden',
      boxSizing: 'border-box',
      '&:hover': {
        boxShadow: `inset 0 -4px 0 ${theme.palette.secondary.main}`,
      },
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.secondary.main}`,
        outlineOffset: -3,
      },
    },
    segmentPassive: {
      cursor: 'default',
      '&:hover': { boxShadow: 'none' },
    },
    railSelected: {
      boxShadow: `inset 0 0 0 3px ${theme.palette.secondary.main}`,
      '&:hover': {
        boxShadow: `inset 0 0 0 3px ${theme.palette.secondary.main}, inset 0 -4px 0 ${theme.palette.secondary.main}`,
      },
    },
  });

const railStateStyles = (theme: Theme) =>
  createStyles({
    evidence: { background: confidenceInk(theme, 'evidence') },
    covered: { background: confidenceInk(theme, 'covered') },
    running: { background: confidenceInk(theme, 'running') },
    detected: { background: confidenceInk(theme, 'detected') },
    wait: { background: confidenceInk(theme, 'wait') },
    // inferred is drawn, not filled: it was never directly observed
    inferred: {
      backgroundColor: 'transparent',
      backgroundImage: `repeating-linear-gradient(135deg, ${confidenceInk(
        theme,
        'inferred',
      )} 0 3px, transparent 3px 8px)`,
    },
    // hatched, never filled: an unobserved interval is not a small value
    void: absenceHatch(theme),
  });

const railSummaryStyles = (theme: Theme) =>
  createStyles({
    railSummary: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      gap: theme.spacing(3),
      margin: theme.spacing(0, 0, 2),
    },
    railSummaryItem: {
      minWidth: 0,
      '& dt': {
        ...cockpitType.eyebrow,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.75),
      },
      '& dd': { margin: 0 },
    },
    railSummaryValue: {
      ...cockpitType.numeral,
      display: 'block',
      color: theme.palette.text.primary,
    },
    railSummaryShare: {
      display: 'block',
      marginTop: theme.spacing(0.25),
      color: theme.palette.text.secondary,
    },
    railAxis: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      listStyle: 'none',
      margin: theme.spacing(1, 0, 0),
      padding: 0,
      color: theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      fontVariantNumeric: 'tabular-nums',
    },
    railTick: { minWidth: 0, overflowWrap: 'anywhere' },
    railTickStart: { textAlign: 'left' },
    railTickMiddle: { textAlign: 'center' },
    railTickEnd: { textAlign: 'right' },
  });

const railDetailStyles = (theme: Theme) =>
  createStyles({
    railLegend: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      listStyle: 'none',
      margin: theme.spacing(2, 0, 0),
      padding: 0,
      color: theme.palette.text.secondary,
    },
    railLegendItem: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.75),
    },
    railLegendMarker: {
      width: theme.spacing(2),
      height: theme.spacing(1.25),
      borderRadius: 2,
      border: `1px solid ${alpha(theme.palette.text.primary, 0.22)}`,
    },
  });

const confidenceStyles = (theme: Theme) =>
  createStyles({
    confidenceDisplay: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
    },
    confidenceMarker: {
      position: 'relative',
      display: 'inline-block',
      width: theme.spacing(1.25),
      height: theme.spacing(1.25),
      flex: '0 0 auto',
      color: theme.palette.text.primary,
      background: 'currentColor',
      boxSizing: 'border-box',
    },
    confidenceConfirmed: { borderRadius: '50%' },
    confidenceCalculated: { borderRadius: 1 },
    confidenceEstimated: { transform: 'rotate(45deg)', borderRadius: 1 },
    confidenceInferred: { transform: 'rotate(45deg)', borderRadius: 1 },
    confidenceDetected: {
      borderRadius: '50%',
      border: '2px solid currentColor',
      background: 'transparent',
    },
    confidencePartial: {
      height: theme.spacing(0.625),
      borderRadius: '8px 8px 0 0',
    },
    confidenceUnavailable: { height: 2, marginTop: 4 },
    confidenceLoadFailed: {
      background: 'transparent',
      '&::before, &::after': {
        content: '""',
        position: 'absolute',
        top: 4,
        left: 0,
        width: '100%',
        height: 2,
        background: 'currentColor',
      },
      '&::before': { transform: 'rotate(45deg)' },
      '&::after': { transform: 'rotate(-45deg)' },
    },
  });

// Nodes are rows on a shared rule, not floating cards: a lane with one item and
// a lane with five then read as the same object at different lengths.
const flowNodeStyles = (theme: Theme) =>
  createStyles({
    node: {
      width: '100%',
      textAlign: 'left',
      display: 'grid',
      gap: theme.spacing(0.25),
      padding: theme.spacing(1.25, 1.5),
      border: 0,
      borderLeft: `3px solid ${alpha(theme.palette.text.primary, 0.18)}`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      background: 'transparent',
      color: theme.palette.text.primary,
      cursor: 'pointer',
      boxSizing: 'border-box',
      transition: 'background-color 0.15s ease, border-color 0.15s ease',
      '&:hover': {
        background: theme.palette.action.hover,
        borderLeftColor: theme.palette.text.primary,
      },
      '&:focus-visible': {
        outline: `3px solid ${theme.palette.secondary.main}`,
        outlineOffset: -3,
      },
    },
    selected: {
      borderLeftColor: theme.palette.secondary.main,
      background: alpha(theme.palette.secondary.main, 0.06),
      '&:hover': {
        background: alpha(theme.palette.secondary.main, 0.1),
        borderLeftColor: theme.palette.secondary.main,
      },
    },
    dimmed: {
      color: theme.palette.text.secondary,
      opacity: 0.55,
      borderLeftColor: theme.palette.divider,
    },
  });

const flowLayoutStyles = (theme: Theme) =>
  createStyles({
    lanes: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: theme.spacing(3),
      marginTop: theme.spacing(2),
    },
    // alignContent:start keeps a one-item lane from stretching to match a
    // five-item one, which is what made the Skills column read as broken
    lane: { display: 'grid', gap: 0, alignContent: 'start', minWidth: 0 },
    laneTitle: {
      ...cockpitType.eyebrow,
      color: theme.palette.text.secondary,
      paddingBottom: theme.spacing(1),
      borderBottom: `1px solid ${theme.palette.text.primary}`,
    },
    nodeLabel: {
      display: 'block',
      overflowWrap: 'anywhere',
      fontWeight: 600,
    },
    nodeLine: { display: 'block', overflowWrap: 'anywhere' },
    nodeSelectionReserved: { display: 'none' },
    nodeSelectionVisible: { display: 'block' },
    nodeMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      color: theme.palette.text.secondary,
    },
    technical: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '0.9em',
      overflowWrap: 'anywhere',
    },
    laneFooter: {
      display: 'block',
      paddingTop: theme.spacing(1),
      color: theme.palette.text.secondary,
    },
  });

export const evidenceStyles = (theme: Theme) =>
  createStyles({
    ...railLayoutStyles(theme),
    ...railStateStyles(theme),
    ...railSummaryStyles(theme),
    ...railDetailStyles(theme),
    ...confidenceStyles(theme),
    ...flowNodeStyles(theme),
    ...flowLayoutStyles(theme),
  });
